import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Send, Loader2, CheckCircle2, XCircle, Search, MessageSquareText,
  ImagePlus, Download, Copy, X, ExternalLink, MousePointerClick, FileCheck2, Users,
} from "lucide-react";

const VARS = ["{nome}", "{whatsapp}", "{email}"];

function renderPreview(msg, contact) {
  if (!contact) return msg;
  return msg
    .replace(/\{nome\}/g, contact.name || "")
    .replace(/\{whatsapp\}/g, contact.whatsapp || "")
    .replace(/\{email\}/g, contact.email || "");
}

function waLink(contact, message) {
  const digits = (contact.whatsapp || "").replace(/\D/g, "");
  const text = encodeURIComponent(renderPreview(message, contact));
  return `https://wa.me/${digits}?text=${text}`;
}

export default function ManualSend() {
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState("");
  const [allClients, setAllClients] = useState([]);
  const [selected, setSelected] = useState([]); // [{id,name,whatsapp,email}]
  const [message, setMessage] = useState("");
  const [photo, setPhoto] = useState(null); // {url, name, blob}
  const [opened, setOpened] = useState([]); // ids already opened
  const [sending, setSending] = useState(false);
  const [report, setReport] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [batchQty, setBatchQty] = useState(50);
  const [batchInterval, setBatchInterval] = useState(30);
  const [batchTpl, setBatchTpl] = useState("");
  const [batchStatus, setBatchStatus] = useState(null);

  useEffect(() => { api.get("/whatsapp/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/whatsapp/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {}); }, []);

  const fetchBatchStatus = () => api.get("/whatsapp/batch-status").then((r) => setBatchStatus(r.data)).catch(() => {});
  useEffect(() => {
    fetchBatchStatus();
    const t = setInterval(fetchBatchStatus, 3000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    api.get("/clients?filter=com_whatsapp").then((r) => setAllClients(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allClients;
    return allClients.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.whatsapp || "").includes(q) ||
      (c.email || "").toLowerCase().includes(q)
    );
  }, [allClients, query]);

  const isSel = (id) => selected.some((s) => s.id === id);
  const toggle = (cl) => setSelected((s) => (isSel(cl.id) ? s.filter((x) => x.id !== cl.id) : [...s, { id: cl.id, name: cl.name, whatsapp: cl.whatsapp, email: cl.email }]));
  const selectAllFiltered = () => setSelected((s) => {
    const map = new Map(s.map((x) => [x.id, x]));
    filtered.forEach((c) => map.set(c.id, { id: c.id, name: c.name, whatsapp: c.whatsapp, email: c.email }));
    return Array.from(map.values());
  });
  const insertVar = (v) => setMessage((m) => `${m}${v}`);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Selecione uma imagem"); return; }
    setPhoto({ url: URL.createObjectURL(f), name: f.name, blob: f });
  };
  const copyPhoto = async () => {
    if (!photo) return;
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ [photo.blob.type]: photo.blob })]);
      toast.success("Foto copiada! Cole no WhatsApp com Ctrl+V.");
    } catch {
      toast.error("Seu navegador não permite copiar imagem. Use o botão Baixar.");
    }
  };

  const openWa = (c) => {
    if (!message.trim()) { toast.error("Escreva a mensagem primeiro"); return; }
    window.open(waLink(c, message), "_blank");
    setOpened((o) => (o.includes(c.id) ? o : [...o, c.id]));
  };

  const startBatch = async () => {
    const q = parseInt(batchQty) || 0;
    const iv = parseInt(batchInterval);
    if (!batchTpl) { toast.error("Escolha um template aprovado"); return; }
    if (q < 1) { toast.error("Informe a quantidade"); return; }
    try {
      const r = await api.post("/whatsapp/batch-send", { template_name: batchTpl, template_language: "pt_BR", quantity: q, interval: isNaN(iv) ? 2 : iv });
      toast.success(`Lote iniciado para ${r.data.quantity} contato(s)!`);
      fetchBatchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao iniciar lote"); }
  };
  const cancelBatch = async () => {
    try { await api.post("/whatsapp/batch-cancel"); toast.success("Lote cancelado."); fetchBatchStatus(); } catch { /* noop */ }
  };

  const sendTemplate = async () => {
    if (!tplName) { toast.error("Escolha um template aprovado"); return; }
    if (selected.length === 0) { toast.error("Selecione ao menos um contato"); return; }
    setSending(true); setReport(null);
    try {
      const r = await api.post("/whatsapp/send-manual-template", { client_ids: selected.map((s) => s.id), template_name: tplName, language: "pt_BR" });
      setReport(r.data);
      if (r.data.sent > 0) toast.success(`${r.data.sent} enviada(s) por template!`);
      if (r.data.errors > 0) toast.error(`${r.data.errors} falha(s).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar");
    } finally { setSending(false); }
  };

  const send = async () => {
    if (!message.trim()) { toast.error("Escreva a mensagem"); return; }
    if (selected.length === 0) { toast.error("Selecione ao menos um contato"); return; }
    setSending(true); setReport(null);
    try {
      const r = await api.post("/whatsapp/send-manual", { client_ids: selected.map((s) => s.id), message });
      setReport(r.data);
      if (r.data.sent > 0) toast.success(`${r.data.sent} mensagem(ns) enviada(s)!`);
      if (r.data.errors > 0) toast.error(`${r.data.errors} falha(s) no envio.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar");
    } finally { setSending(false); }
  };

  const sendWithImage = async () => {
    if (!photo) { toast.error("Anexe uma foto primeiro"); return; }
    if (!message.trim()) { toast.error("Escreva a legenda/mensagem"); return; }
    if (selected.length === 0) { toast.error("Selecione ao menos um contato"); return; }
    setSending(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append("client_ids", JSON.stringify(selected.map((s) => s.id)));
      fd.append("message", message);
      fd.append("image", photo.blob, photo.name);
      const r = await api.post("/whatsapp/send-manual-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      if (r.data.sent > 0) toast.success(`${r.data.sent} mensagem(ns) com imagem enviada(s)!`);
      if (r.data.errors > 0) toast.error(`${r.data.errors} falha(s) no envio.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar");
    } finally { setSending(false); }
  };

  const approvedTemplates = templates.filter((t) => t.status === "APPROVED" && t.name !== "hello_world");
  const batchJob = batchStatus?.job;
  const batchRunning = batchJob?.status === "running";
  const selectedTplPreview = (() => {
    const t = approvedTemplates.find((x) => x.name === tplName);
    if (!t) return "";
    const body = (t.components || []).find((c) => c.type === "BODY");
    return body ? body.text : "";
  })();

  return (
    <div className="space-y-6 animate-fade-up max-w-4xl" data-testid="manual-send-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Envio Manual</h1>
        <p className="text-sm text-slate-500 mt-1">Selecione os contatos, escreva a mensagem, anexe uma foto e abra o WhatsApp com o texto pronto.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Contacts */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-800">Contatos</h3>
            <span className="text-xs text-emerald-700 font-medium">{selected.length} selecionado(s)</span>
          </div>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nome, WhatsApp ou e-mail..." className="pl-9 h-9" data-testid="manual-search" />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={selectAllFiltered} data-testid="manual-select-all">Selecionar todos ({filtered.length})</Button>
            {selected.length > 0 && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setSelected([])} data-testid="manual-clear-selection">Limpar</Button>}
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded">
            {filtered.length === 0 && <p className="text-xs text-slate-400 p-3 text-center">Nenhum contato encontrado.</p>}
            {filtered.map((cl) => {
              const active = isSel(cl.id);
              return (
                <button type="button" key={cl.id} onClick={() => toggle(cl)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 ${active ? "bg-emerald-50" : ""}`}
                  data-testid={`manual-contact-${cl.id}`}>
                  <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${active ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>
                    {active && <CheckCircle2 className="h-3 w-3 text-white" />}
                  </span>
                  <span className="flex-1 truncate">{cl.name}</span>
                  <span className="text-xs text-slate-400">{cl.whatsapp}</span>
                </button>
              );
            })}
          </div>
          {filtered.length > 0 && <p className="text-[11px] text-slate-400">Mostrando {filtered.length} contato(s). Use "Selecionar todos" ou refine a busca.</p>}
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {selected.slice(0, 30).map((s) => (
                <span key={s.id} className="text-xs bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 flex items-center gap-1">
                  {s.name}
                  <button onClick={() => toggle(s)} className="hover:text-emerald-900" data-testid={`manual-remove-${s.id}`}>×</button>
                </span>
              ))}
              {selected.length > 30 && <span className="text-xs text-slate-400 px-1 py-0.5">+{selected.length - 30} mais</span>}
            </div>
          )}
        </div>

        {/* Message + photo */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><MessageSquareText className="h-4 w-4 text-emerald-600" /> Mensagem</h3>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} placeholder="Escreva sua mensagem... Use {nome} para personalizar." data-testid="manual-message" />
          <div className="flex items-center justify-between">
            <div className="flex gap-2 flex-wrap">{VARS.map((v) => <button key={v} onClick={() => insertVar(v)} className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 font-mono border border-emerald-100">{v}</button>)}</div>
            <span className={`text-xs ${message.length > 1000 ? "text-red-500" : "text-slate-400"}`} data-testid="manual-char-count">{message.length} caracteres</span>
          </div>

          {/* Photo attach */}
          <div className="pt-2 border-t border-slate-100">
            {!photo ? (
              <label className="flex items-center justify-center gap-2 h-24 rounded-lg border-2 border-dashed border-slate-200 text-slate-400 text-sm cursor-pointer hover:border-emerald-300 hover:text-emerald-600 transition-colors" data-testid="manual-photo-dropzone">
                <ImagePlus className="h-5 w-5" /> Anexar foto (opcional)
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="manual-photo-input" />
              </label>
            ) : (
              <div className="space-y-2" data-testid="manual-photo-preview">
                <div className="relative">
                  <img src={photo.url} alt="anexo" className="w-full max-h-56 object-contain rounded-lg border border-slate-200 bg-slate-50" />
                  <button onClick={() => setPhoto(null)} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 border border-slate-200 flex items-center justify-center text-slate-500 hover:text-red-500" data-testid="manual-photo-remove"><X className="h-4 w-4" /></button>
                </div>
                <div className="flex gap-2">
                  <a href={photo.url} download={photo.name} className="flex-1"><Button type="button" variant="outline" className="w-full" data-testid="manual-photo-download"><Download className="h-4 w-4 mr-1" /> Baixar</Button></a>
                  <Button type="button" variant="outline" className="flex-1" onClick={copyPhoto} data-testid="manual-photo-copy"><Copy className="h-4 w-4 mr-1" /> Copiar</Button>
                </div>
                <p className="text-[11px] text-slate-400">Depois de abrir a conversa, cole (Ctrl+V) ou anexe a foto baixada no WhatsApp.</p>
              </div>
            )}
          </div>

          {selected.length > 0 && message.trim() && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="manual-preview">
              <p className="text-[11px] font-medium text-slate-500 mb-1">Prévia para <span className="text-emerald-700">{selected[0].name}</span>{selected.length > 1 && <span className="text-slate-400"> (e mais {selected.length - 1})</span>}</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{renderPreview(message, selected[0])}</p>
            </div>
          )}
        </div>
      </div>

      {/* Manual WhatsApp open */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3" data-testid="manual-whatsapp-open">
        <div className="flex items-center gap-2">
          <MousePointerClick className="h-4 w-4 text-emerald-600" />
          <h3 className="font-semibold text-slate-800">Abrir no WhatsApp (você clica enviar)</h3>
          {selected.length > 0 && <span className="text-xs text-slate-400">{opened.length}/{selected.length} abertos</span>}
        </div>
        <p className="text-xs text-slate-500">Abra a conversa de cada contato com a mensagem já preenchida. Anexe a foto (se houver) e clique em enviar dentro do WhatsApp.</p>
        {selected.length === 0 ? (
          <p className="text-xs text-slate-400 py-4 text-center">Selecione ao menos um contato acima.</p>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-100 rounded max-h-72 overflow-y-auto">
            {selected.slice(0, 200).map((c) => {
              const done = opened.includes(c.id);
              return (
                <div key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  {done ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <span className="h-4 w-4 rounded-full border border-slate-300 shrink-0" />}
                  <span className="flex-1 truncate">{c.name} <span className="text-xs text-slate-400">{c.whatsapp}</span></span>
                  <Button size="sm" onClick={() => openWa(c)} className={done ? "bg-slate-400 hover:bg-slate-500" : "bg-emerald-500 hover:bg-emerald-600"} data-testid={`manual-open-wa-${c.id}`}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> {done ? "Abrir de novo" : "Abrir WhatsApp"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        {selected.length > 200 && <p className="text-[11px] text-slate-400">Mostrando 200 de {selected.length} selecionados. Para abrir milhares um a um não é prático — use o envio automático pela API abaixo.</p>}
      </div>

      {/* API send (image + caption in one message) */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3" data-testid="manual-api-send">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-sky-600" />
          <h3 className="font-semibold text-slate-800">Enviar automático pela API oficial (imagem + texto juntos)</h3>
        </div>
        <p className="text-xs text-slate-500">Envia numa única mensagem a imagem com a legenda para todos os contatos selecionados — igual ao WhatsApp. Requer token válido da Meta.</p>
        {status && !status.connected && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800" data-testid="manual-api-warning">
            {status.token_error || "Token da Meta pendente"} — configure/gere o token na aba <strong>WhatsApp</strong> para liberar o envio automático.
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-2">
          <Button onClick={sendWithImage} disabled={sending || !photo} className="bg-emerald-500 hover:bg-emerald-600" data-testid="manual-send-image-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ImagePlus className="h-4 w-4 mr-2" />}
            Enviar imagem + texto{selected.length ? ` (${selected.length})` : ""}
          </Button>
          <Button onClick={send} disabled={sending} variant="outline" data-testid="manual-send-button">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Enviar só texto{selected.length ? ` (${selected.length})` : ""}
          </Button>
        </div>
        {!photo && <p className="text-[11px] text-slate-400">Anexe uma foto acima para habilitar o envio de imagem + texto.</p>}
      </div>

      {/* Template send (reaches everyone, bypasses 24h window) */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3" data-testid="manual-template-send">
        <div className="flex items-center gap-2">
          <FileCheck2 className="h-4 w-4 text-violet-600" />
          <h3 className="font-semibold text-slate-800">Enviar por template aprovado (chega em toda a base)</h3>
        </div>
        <p className="text-xs text-slate-500">Templates aprovados pela Meta são entregues mesmo para quem não te escreveu nas últimas 24h. Use este modo para disparos em massa.</p>
        {approvedTemplates.length === 0 ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800" data-testid="manual-template-empty">
            {templates.filter((t) => t.name !== "hello_world").length === 0
              ? "Nenhum template próprio ainda. Já enviei o seu para aprovação da Meta."
              : "Seu template está em análise pela Meta (PENDING). Assim que for aprovado, ele aparece aqui para disparo."}
          </div>
        ) : (
          <>
            <select value={tplName} onChange={(e) => setTplName(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="manual-template-select">
              <option value="">Escolha um template aprovado...</option>
              {approvedTemplates.map((t) => <option key={t.name + t.language} value={t.name}>{t.name} ({t.language})</option>)}
            </select>
            {selectedTplPreview && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="manual-template-preview">
                <p className="text-[11px] font-medium text-slate-500 mb-1">Prévia do template</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedTplPreview}</p>
              </div>
            )}
            <Button onClick={sendTemplate} disabled={sending || !tplName} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="manual-send-template-button">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileCheck2 className="h-4 w-4 mr-2" />}
              Enviar template{selected.length ? ` (${selected.length})` : ""}
            </Button>
          </>
        )}
      </div>

      {/* Envio em lote automático com rodízio */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3" data-testid="manual-batch-send">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-indigo-600" />
          <h3 className="font-semibold text-slate-800">Envio em lote automático (com rodízio)</h3>
        </div>
        <p className="text-xs text-slate-500">Defina quantos contatos enviar e o tempo entre cada mensagem. Quem recebe fica bloqueado até TODOS os cadastrados receberem; depois o rodízio reinicia sozinho.</p>
        {batchStatus && (
          <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-2.5 text-xs text-indigo-800" data-testid="batch-availability">
            Ciclo atual: <strong>{batchStatus.done}/{batchStatus.total}</strong> já receberam · <strong>{batchStatus.remaining}</strong> disponíveis
            {batchJob?.cycle_reset && batchStatus.remaining === batchStatus.total && <span className="block mt-1 text-emerald-700">✓ Todos receberam! O rodízio foi reiniciado — tudo liberado de novo.</span>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">Quantidade de contatos</label>
            <Input type="number" min={1} value={batchQty} onChange={(e) => setBatchQty(e.target.value)} disabled={batchRunning} data-testid="batch-quantity" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Intervalo entre mensagens (s)</label>
            <Input type="number" min={0} value={batchInterval} onChange={(e) => setBatchInterval(e.target.value)} disabled={batchRunning} data-testid="batch-interval" />
          </div>
        </div>
        {approvedTemplates.length === 0 ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">Nenhum template aprovado disponível ainda.</div>
        ) : (
          <select value={batchTpl} onChange={(e) => setBatchTpl(e.target.value)} disabled={batchRunning} className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="batch-template-select">
            <option value="">Escolha o template aprovado...</option>
            {approvedTemplates.map((t) => <option key={t.name + t.language} value={t.name}>{t.name} ({t.language})</option>)}
          </select>
        )}
        {batchRunning ? (
          <div className="space-y-2" data-testid="batch-progress">
            <p className="text-sm text-slate-700">Enviando... <strong>{batchJob?.sent || 0}</strong> enviadas · <span className="text-red-500">{batchJob?.errors || 0} erros</span> de {batchJob?.total || 0}</p>
            {batchJob?.current && <p className="text-xs text-slate-400">Atual: {batchJob.current.name} ({batchJob.current.whatsapp})</p>}
            <Button variant="outline" onClick={cancelBatch} className="w-full text-red-600 border-red-200" data-testid="batch-cancel"><X className="h-4 w-4 mr-1" /> Cancelar lote</Button>
          </div>
        ) : (
          <Button onClick={startBatch} disabled={!batchTpl || approvedTemplates.length === 0} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="batch-send-button">
            <Users className="h-4 w-4 mr-2" /> Enviar lote
          </Button>
        )}
      </div>

      {report && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="manual-report">
          <h3 className="font-semibold text-slate-800 mb-3">Resultado — {report.sent} enviada(s), {report.errors} falha(s)</h3>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {report.results.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm py-1 border-b border-slate-50 last:border-0">
                {r.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
                <span className="flex-1 truncate">{r.name} <span className="text-slate-400">{r.whatsapp}</span></span>
                {!r.ok && <span className="text-xs text-red-500">{r.reason}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
